import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./shared/lib/sentry";
import { AppProviders } from "./app/providers";
import { AppRouter } from "./app/router/AppRouter";
import { supabase } from "./shared/lib/supabase";
import { useAuthStore } from "./app/store/auth.store";
import { HelmetProvider } from "react-helmet-async";
import { FullScreenLoader } from "@/shared/ui/components/FullScreenLoader";
import "./index.css";
import "./i18n/config";
import { EnvironmentBadge } from "./shared/ui/components/EnvironmentBadge";
import { registerStaleChunkRecovery } from "./shared/lib/stale-chunk-reload";

registerStaleChunkRecovery();

if (typeof window !== "undefined") {
  const url = new URL(window.location.href);
  if (url.searchParams.has("_chunk_reload")) {
    url.searchParams.delete("_chunk_reload");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }
}

function AuthInitializer({ children }: { children: React.ReactNode }) {
  const _hasHydrated = useAuthStore((s) => s._hasHydrated);
  const isInitializing = useAuthStore((s) => s.isInitializing);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { setToken, setInitializing, resetSessionContext } = useAuthStore();

  useEffect(() => {
    // Si Zustand no se ha hidratado del localStorage, esperamos.
    if (!_hasHydrated) return;

    let isMounted = true;

    const initAuth = async () => {
      try {
        console.log("🚀 Iniciando comprobación de Auth...");
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) throw error;

        if (session && isMounted) {
          console.log("✅ Sesión encontrada:", session.user.email);
          setToken(session.access_token);
        } else if (isMounted) {
          // Never trust a persisted Zustand profile when Supabase has no session.
          // This prevents a signed-out tenant account from leaking into patient flows.
          resetSessionContext();
        }
      } catch (err) {
        console.error("💥 Fallo crítico en initAuth:", err);
      } finally {
        if (isMounted) {
          setInitializing(false);
        }
      }
    };

    initAuth();

    // Suscripción reactiva a cambios de sesión (login, logout, token expirado)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("🔔 Evento Supabase:", event);
      // Supabase can emit SIGNED_IN again when an existing session is restored.
      // Clearing the cache in that case made the entire app look like it reloaded
      // whenever the browser tab regained focus.
      if (event === "SIGNED_IN" && session?.user.id !== currentUserId) {
        resetSessionContext();
      }
      if (session) {
        setToken(session.access_token);
      }
      if (event === "SIGNED_OUT") {
        // The explicit UI logout already signs out Supabase. Calling it again
        // from this notification would emit another SIGNED_OUT indefinitely.
        resetSessionContext();
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [
    _hasHydrated,
    currentUserId,
    setToken,
    setInitializing,
    resetSessionContext,
  ]);

  // Pantalla de carga unificada para el arranque técnico de la app
  if (!_hasHydrated || isInitializing) {
    return <FullScreenLoader />;
  }

  return <>{children}</>;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppProviders>
      <HelmetProvider prioritizeSeoTags>
        <AuthInitializer>
          <EnvironmentBadge />
          <AppRouter />
        </AuthInitializer>
      </HelmetProvider>
    </AppProviders>
  </React.StrictMode>,
);
